import type { RefObject } from "react"
import {
  AssigneePicker,
  type AssigneePickerData,
  type PickerAssignee,
} from "@/components/v2/assignees/assignee-picker"
import {
  fetchDealAssignees,
  updateDealAssignment,
} from "@/lib/deal-assignment-api"
import { replaceDealProp, type DealPropKey } from "@/lib/deal-prop-update"
import type { DealDrawer as DealDrawerData } from "@/types/v2/conversation"
import type { DealOwner } from "@/types/v2/deal"

// Deal-flavored wiring for the shared AssigneePicker:
// loads teammates from /v2/deals/assignees, PATCHes
// /v2/deals/:id/assignment, and (for the drawer/show
// surfaces) optimistically patches the deal payload's
// `owner` + `owners` via `replaceDealProp`.
//
// The kanban card uses `fetchDealAssigneeOptions` +
// `updateDealAssignment` directly with its own pipeline-prop
// optimistic update (see deal-card.tsx).

// Neutral disc for legacy payloads that ship only the
// single `owner` (no per-employee color).
const FALLBACK_OWNER_COLOR = "#C9C2B6"

// The deal's owners as stack people. Prefers the full
// multi-owner set; falls back to the single `owner` for
// payloads that predate CON-218.
export function dealOwnersList(deal: DealDrawerData): DealOwner[] {
  if (deal.owners) return deal.owners
  if (!deal.owner) return []
  return [{ ...deal.owner, color: FALLBACK_OWNER_COLOR }]
}

// Maps the deals presenter's `dealsCount` onto the picker's
// generic `count` ("Sales · 3 deals").
export function fetchDealAssigneeOptions(
  signal?: AbortSignal,
): Promise<AssigneePickerData> {
  return fetchDealAssignees("", signal).then((data) => ({
    employees: data.employees.map((row) => ({
      id: row.id,
      name: row.name,
      initials: row.initials,
      color: row.color,
      role: row.role,
      email: row.email,
      count: row.dealsCount,
    })),
  }))
}

// Owner picker for the deal drawer / show / mobile show
// surfaces. Applies the optimistic owner set to the deal
// payload under `dealPropKey` and reverts on failure (the
// picker re-calls `applyOptimistic` with the snapshot).
export function DealOwnersPicker({
  deal,
  dealPropKey,
  open,
  onClose,
  triggerRef,
}: {
  deal: DealDrawerData
  dealPropKey: DealPropKey
  open: boolean
  onClose: () => void
  triggerRef: RefObject<HTMLElement | null>
}) {
  const applyOptimistic = (assignees: PickerAssignee[]) => {
    replaceDealProp(dealPropKey, (current) => ({
      ...current,
      owner: assignees[0]
        ? {
            id: assignees[0].id,
            name: assignees[0].name,
            initials: assignees[0].initials,
          }
        : null,
      owners: assignees,
    }))
  }

  return (
    <AssigneePicker
      open={open}
      onClose={onClose}
      triggerRef={triggerRef}
      entityLabel="deal"
      contextName={deal.name}
      countNoun="deal"
      testIdPrefix="reassign-deal"
      fetchOptions={fetchDealAssigneeOptions}
      currentAssignees={dealOwnersList(deal)}
      applyOptimistic={applyOptimistic}
      onSave={(employeeIds) =>
        updateDealAssignment(deal.id, employeeIds)
      }
    />
  )
}
